/**
 * Centralized Date Formatter Utility
 * Standard format: DD MON YYYY (e.g., 07 Jan 2026)
 */

/**
 * Month abbreviations
 */
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Format a date to DD MON YYYY format (e.g., 07 Jan 2026)
 * @param date - Date object, string, or null/undefined
 * @returns Formatted date string or empty string if invalid
 */
export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return '';

  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = MONTH_NAMES[dateObj.getMonth()];
  const year = dateObj.getFullYear();

  return `${day} ${month} ${year}`;
}

/**
 * Format a date to DD MON YYYY HH:MM AM/PM format (e.g., 07 Jan 2026 10:30 AM)
 * @param date - Date object, string, or null/undefined
 * @returns Formatted date-time string or empty string if invalid
 */
export function formatDateTime(date: Date | string | null | undefined): string {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return '';

  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = MONTH_NAMES[dateObj.getMonth()];
  const year = dateObj.getFullYear();

  let hours = dateObj.getHours();
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12; // 0 should be 12

  return `${day} ${month} ${year} ${hours}:${minutes} ${ampm}`;
}

/**
 * AG-Grid Value Formatter for dates
 * Use this in column definitions: valueFormatter: dateValueFormatter
 */
export function dateValueFormatter(params: any): string {
  return formatDate(params.value);
}

/**
 * AG-Grid Value Formatter for date-time
 * Use this in column definitions: valueFormatter: dateTimeValueFormatter
 */
export function dateTimeValueFormatter(params: any): string {
  return formatDateTime(params.value);
}

/**
 * Parse a date string in DD MON YYYY format to Date object
 * @param dateString - Date string in DD MON YYYY format
 * @returns Date object or null if invalid
 */
export function parseDate(dateString: string): Date | null {
  if (!dateString) return null;

  const parts = dateString.split(' ');
  if (parts.length !== 3) return null;

  const day = parseInt(parts[0], 10);
  const monthIndex = MONTH_NAMES.findIndex(m => m.toLowerCase() === parts[1].toLowerCase());
  const year = parseInt(parts[2], 10);

  if (monthIndex === -1) return null;

  const date = new Date(year, monthIndex, day);

  if (isNaN(date.getTime())) return null;

  return date;
}

/**
 * Date format constant for use across the application
 */
export const DATE_FORMAT = 'DD MON YYYY';
export const DATE_TIME_FORMAT = 'DD MON YYYY HH:MM AM/PM';
